/* Isshobi wiki search
   The data comes from sections/wiki-search-index.liquid through the Section Rendering API,
   fetched the first time someone focuses the box, then matched in the browser. */

(() => {
  const MAX_PER_GROUP = 8;

  const normalize = (text) =>
    (text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();

  const fetchSection = (url) =>
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));

  class WikiSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-wiki-results]');
      this.status = this.querySelector('[data-wiki-status]');
      this.hidden = false;

      this.input.addEventListener('focus', () => this.load(), { once: true });
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 120);
      });
      this.querySelector('form').addEventListener('submit', (event) => {
        event.preventDefault();
        const first = this.results.querySelector('a');
        if (first) window.location.assign(first.href);
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    load() {
      this.entries ??= fetchSection(this.dataset.indexUrl)
        .then((doc) => JSON.parse(doc.querySelector('[data-wiki-index]').textContent))
        .then((entries) =>
          entries.map((entry) => ({
            ...entry,
            name: normalize(entry.n),
            meta: normalize(`${entry.r} ${entry.p}`),
            summary: normalize(entry.s),
            text: normalize(entry.x),
          }))
        )
        .catch(() => []);
      return this.entries;
    }

    async search() {
      const query = this.input.value.trim();
      if (!query) {
        this.clear();
        return;
      }

      const entries = await this.load();
      if (this.input.value.trim() !== query) return;

      const terms = normalize(query).split(/\s+/);
      const matches = entries
        .map((entry) => ({ entry, score: this.score(entry, terms) }))
        .filter((match) => match.score > 0)
        .sort((a, b) => b.score - a.score || a.entry.n.localeCompare(b.entry.n))
        .map((match) => match.entry);

      this.render(matches, query);
    }

    // Every word has to match somewhere; a match in the name counts most.
    score(entry, terms) {
      let total = 0;
      for (const term of terms) {
        let best = 0;
        if (entry.name.startsWith(term)) best = 10;
        else if (entry.name.includes(term)) best = 6;
        else if (entry.meta.includes(term)) best = 3;
        else if (entry.summary.includes(term)) best = 2;
        else if (entry.text.includes(term)) best = 1;
        if (!best) return 0;
        total += best;
      }
      return total;
    }

    render(matches, query) {
      this.results.replaceChildren();

      if (!matches.length) {
        const empty = document.createElement('p');
        empty.className = 'isshobi-wiki-search__empty';
        empty.textContent = this.dataset.noResults.replace('[query]', query);
        this.results.append(empty);
        this.status.textContent = empty.textContent;
        this.results.hidden = false;
        return;
      }

      // The group holding the best match comes first. Group labels come from data-label-{type}.
      const types = [...new Set(matches.map((entry) => entry.t))];

      types.forEach((type) => {
        const id = `${this.results.id}-${type}`;
        const heading = document.createElement('p');
        heading.className = 'isshobi-wiki-search__group';
        heading.id = id;
        heading.textContent = this.getAttribute(`data-label-${type}`) || type;

        const list = document.createElement('ul');
        list.className = 'isshobi-wiki-search__list';
        list.setAttribute('role', 'list');
        list.setAttribute('aria-labelledby', id);

        matches
          .filter((entry) => entry.t === type)
          .slice(0, MAX_PER_GROUP)
          .forEach((entry) => list.append(this.item(entry)));

        this.results.append(heading, list);
      });

      this.status.textContent = this.dataset.results.replace('[count]', matches.length);
      this.results.hidden = false;
    }

    item(entry) {
      const link = document.createElement('a');
      link.className = 'isshobi-wiki-search__link';
      link.href = entry.u;

      const thumb = document.createElement('span');
      thumb.className = 'isshobi-wiki-search__thumb';
      if (entry.i) {
        const image = document.createElement('img');
        image.src = entry.i;
        image.alt = '';
        image.width = 48;
        image.height = 48;
        image.loading = 'lazy';
        thumb.append(image);
      } else {
        thumb.textContent = entry.n.charAt(0);
        thumb.setAttribute('aria-hidden', 'true');
      }

      const name = document.createElement('span');
      name.className = 'isshobi-wiki-search__name';
      name.textContent = entry.n;

      const text = document.createElement('span');
      text.className = 'isshobi-wiki-search__text';
      text.append(name);

      // Role, kind or domains when the type has one, otherwise the linked place.
      const meta = entry.r || entry.p;
      if (meta) {
        const metaElement = document.createElement('span');
        metaElement.className = 'isshobi-wiki-search__meta';
        metaElement.textContent = meta;
        text.append(metaElement);
      }

      link.append(thumb, text);
      const item = document.createElement('li');
      item.append(link);
      return item;
    }

    clear() {
      this.results.replaceChildren();
      this.results.hidden = true;
      this.status.textContent = '';
    }

    // Arrow keys move between the box and the results; Escape empties the box.
    onKeydown(event) {
      const links = [...this.results.querySelectorAll('a')];
      const index = links.indexOf(document.activeElement);

      if (event.key === 'Escape' && this.input.value) {
        event.preventDefault();
        this.input.value = '';
        this.clear();
        this.input.focus();
      } else if (event.key === 'ArrowDown' && links.length) {
        event.preventDefault();
        links[Math.min(index + 1, links.length - 1)].focus();
      } else if (event.key === 'ArrowUp' && index !== -1) {
        event.preventDefault();
        (index === 0 ? this.input : links[index - 1]).focus();
      }
    }
  }

  if (!customElements.get('wiki-search')) customElements.define('wiki-search', WikiSearch);
})();
